'use client'

import { useMemo } from 'react'

import { Period, getPeriodLabel } from '~/app/accounting/period-utils'
import {
  CLSize,
  DateInput,
  SelectInput,
  Toggleable,
} from '~/components/component-library'
import { Timestamp } from '~/components/component-library/Timestamp'
import {
  AccountingTz,
  datePickerValueToTz,
  endOfDay,
  tzValueToDatePicker,
} from '~/utils/timezone'

type DateRangeInputProps = {
  period: Period
  presets: Period[]
  setPeriod: (period: Period) => void
  tz: AccountingTz
  onToggleTz: () => void
  onPresetSelect?: () => void
  size?: CLSize
}

/** The shared period preset, editable date range, and timezone controls. */
export const DateRangeInput = ({
  period,
  presets,
  setPeriod,
  tz,
  onToggleTz,
  onPresetSelect,
  size = 'small',
}: DateRangeInputProps) => {
  const selectedPreset = presets.find((preset) => preset.label === period.label)
  const selectOptions = useMemo(
    () =>
      presets.map((preset) => ({
        value: preset.label,
        label: preset.label,
        suffix: preset.startDate ? (
          <span className="text-secondary">
            <Timestamp
              timestamp={preset.startDate.getTime()}
              type="absolute"
              abbreviate
              tz={tz}
            />{' '}
            -{' '}
            {preset.endDate ? (
              <Timestamp
                timestamp={preset.endDate.getTime()}
                type="absolute"
                abbreviate
                tz={tz}
              />
            ) : (
              'Now'
            )}
          </span>
        ) : undefined,
      })),
    [presets, tz],
  )

  const updateDate = (updates: Omit<Period, 'label'>) => {
    setPeriod({ ...updates, label: getPeriodLabel(updates) })
  }

  const todayInPicker = tzValueToDatePicker(new Date(), tz)
  const endInPicker = period.endDate
    ? tzValueToDatePicker(period.endDate, tz)
    : null
  const startMaxDate = endInPicker
    ? new Date(Math.min(endInPicker.getTime(), todayInPicker.getTime()))
    : todayInPicker

  return (
    <>
      <SelectInput
        label="Select a period"
        size={size}
        options={[
          ...selectOptions,
          ...(selectedPreset
            ? []
            : [{ value: 'Custom range', label: 'Custom range' }]),
        ]}
        selectedOption={selectedPreset?.label ?? 'Custom range'}
        selectValue={(label) => {
          const preset = presets.find((option) => option.label === label)
          if (preset) setPeriod(preset)
          onPresetSelect?.()
        }}
        placeholder="Select a period"
      />
      <div className="grid grid-cols-2 gap-4">
        <DateInput
          label="Start date"
          size={size}
          maxDate={startMaxDate}
          value={
            period.startDate ? tzValueToDatePicker(period.startDate, tz) : null
          }
          onChange={(date) => {
            if (date)
              updateDate({
                startDate: datePickerValueToTz(date, tz),
                endDate: period.endDate,
              })
          }}
          onClear={() =>
            updateDate({ startDate: undefined, endDate: period.endDate })
          }
          placeholder="Earliest"
        />
        <DateInput
          label="End date"
          size={size}
          minDate={
            period.startDate
              ? tzValueToDatePicker(period.startDate, tz)
              : undefined
          }
          maxDate={todayInPicker}
          value={
            period.endDate ? tzValueToDatePicker(period.endDate, tz) : null
          }
          onChange={(date) => {
            if (date)
              updateDate({
                startDate: period.startDate,
                endDate: endOfDay(datePickerValueToTz(date, tz), tz),
              })
          }}
          onClear={() =>
            updateDate({ startDate: period.startDate, endDate: undefined })
          }
          placeholder="Today"
        />
      </div>
      <Toggleable
        layout="standard"
        label="Show times in UTC"
        tooltip="When off, times use your local time zone"
        isChecked={tz === 'utc'}
        onCheckedChange={onToggleTz}
      />
    </>
  )
}
